import { useState, useRef } from "react";
import { X, Check } from "lucide-react";

const FORM_ACTION = "https://c0038eab.sibforms.com/serve/MUIFAMeF9t9xEfIY21r6AwcE2f0NR3xxhQKOqzpuLjlOcFayR0f5x9Gs1ZtUQsjQlbqN3sknZL9uBCVBj0pMz9-MOe8Yg7T9rph53gglKUtomm981ixoiC0OIiCxSde4xwaUATlBCn-ZDT90hfaNwqmXo8HePNdWTeVYA2ydVb5CFdJagOPV341qKNZ3WZG36Z0zYMXzbob9q2sU";

export default function CheckDateModal({ isOpen, onClose }) {
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);

    try {
      const formData = new FormData(formRef.current);
      await fetch(FORM_ACTION, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setError(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="check-date-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-[520px] mx-4 max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: "#0A0908", border: "1px solid rgba(194,168,120,0.2)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="check-date-modal-close"
          className="absolute top-4 right-4 z-10 text-[#A19D94] hover:text-[#F5F5F0] transition-colors"
          onClick={handleClose}
        >
          <X size={22} />
        </button>

        <div className="p-6 md:p-8">
          {submitted ? (
            <div data-testid="form-success" className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-[#C2A878]/20 flex items-center justify-center mx-auto mb-4">
                <Check size={28} className="text-[#C2A878]" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl text-[#F5F5F0] tracking-tight mb-2">
                Thank you!
              </h3>
              <p className="text-sm text-[#A19D94] font-sans">
                We'll be in touch within 24 hours to chat about your day.
              </p>
              <button
                onClick={handleClose}
                className="mt-6 px-8 py-3 bg-[#C2A878] text-[#0A0908] text-sm font-sans font-medium tracking-wider uppercase hover:bg-[#d4bc92] transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs tracking-[0.2em] uppercase text-[#C2A878] font-sans mb-2">
                Check Availability
              </p>
              <h3 className="font-serif text-xl sm:text-2xl text-[#F5F5F0] tracking-tight mb-1">
                Request your quote
              </h3>
              <p className="text-sm text-[#A19D94] font-sans mb-6">
                Pop your details in and we'll get back to you within 24 hours.
              </p>

              {error && (
                <div className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-800/30 mb-4">
                  Something went wrong. Please try again.
                </div>
              )}

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[#A19D94] font-sans mb-1.5" htmlFor="FIRSTNAME">First Name *</label>
                    <input className="w-full bg-white/[0.06] border border-white/10 text-[#F5F5F0] text-sm font-sans px-3 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors placeholder:text-[#A19D94]/40" type="text" id="FIRSTNAME" name="FIRSTNAME" autoComplete="given-name" required />
                  </div>
                  <div>
                    <label className="block text-xs text-[#A19D94] font-sans mb-1.5" htmlFor="LASTNAME">Last Name *</label>
                    <input className="w-full bg-white/[0.06] border border-white/10 text-[#F5F5F0] text-sm font-sans px-3 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors placeholder:text-[#A19D94]/40" type="text" id="LASTNAME" name="LASTNAME" autoComplete="family-name" required />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-[#A19D94] font-sans mb-1.5" htmlFor="PARTNER_FIRST_NAME">Partner's First Name *</label>
                  <input className="w-full bg-white/[0.06] border border-white/10 text-[#F5F5F0] text-sm font-sans px-3 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors placeholder:text-[#A19D94]/40" type="text" id="PARTNER_FIRST_NAME" name="PARTNER_FIRST_NAME" autoComplete="off" required />
                </div>

                <div>
                  <label className="block text-xs text-[#A19D94] font-sans mb-1.5" htmlFor="EMAIL">Email Address *</label>
                  <input className="w-full bg-white/[0.06] border border-white/10 text-[#F5F5F0] text-sm font-sans px-3 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors placeholder:text-[#A19D94]/40" type="email" id="EMAIL" name="EMAIL" autoComplete="email" required />
                </div>

                <div>
                  <label className="block text-xs text-[#A19D94] font-sans mb-1.5" htmlFor="SMS">Mobile No. *</label>
                  <div className="flex gap-2">
                    <select className="bg-white/[0.06] border border-white/10 text-[#A19D94] text-sm font-sans px-2 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors w-[90px]" name="SMS__COUNTRY_CODE" defaultValue="+44">
                      <option value="+44">+44</option>
                      <option value="+1">+1</option>
                      <option value="+353">+353</option>
                      <option value="+33">+33</option>
                      <option value="+49">+49</option>
                      <option value="+34">+34</option>
                      <option value="+39">+39</option>
                      <option value="+61">+61</option>
                    </select>
                    <input className="flex-1 bg-white/[0.06] border border-white/10 text-[#F5F5F0] text-sm font-sans px-3 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors placeholder:text-[#A19D94]/40" type="tel" id="SMS" name="SMS" autoComplete="tel" placeholder="Phone number" required />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-[#A19D94] font-sans mb-1.5" htmlFor="DATE_OF_WEDDING">Wedding Date *</label>
                    <input className="w-full bg-white/[0.06] border border-white/10 text-[#F5F5F0] text-sm font-sans px-3 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors placeholder:text-[#A19D94]/40" type="text" id="DATE_OF_WEDDING" name="DATE_OF_WEDDING" autoComplete="off" placeholder="dd-mm-yyyy" required />
                  </div>
                  <div>
                    <label className="block text-xs text-[#A19D94] font-sans mb-1.5" htmlFor="NAME_OF_VENUE">Wedding Venue *</label>
                    <input className="w-full bg-white/[0.06] border border-white/10 text-[#F5F5F0] text-sm font-sans px-3 py-2.5 focus:border-[#C2A878]/50 focus:outline-none transition-colors placeholder:text-[#A19D94]/40" type="text" id="NAME_OF_VENUE" name="NAME_OF_VENUE" autoComplete="off" required />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-[#A19D94] font-sans mb-2">Package Required *</label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input type="checkbox" className="w-4 h-4 accent-[#C2A878] bg-white/[0.06] border-white/10" name="PACKAGE_TYPE[]" value="Videography Package" />
                      <span className="text-sm text-[#A19D94] font-sans group-hover:text-[#F5F5F0] transition-colors">Videography Package</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <input type="checkbox" className="w-4 h-4 accent-[#C2A878] bg-white/[0.06] border-white/10" name="PACKAGE_TYPE[]" value="Videography & Photography Package" />
                      <span className="text-sm text-[#A19D94] font-sans group-hover:text-[#F5F5F0] transition-colors">Videography & Photography Package</span>
                    </label>
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer group pt-2">
                  <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[#C2A878] bg-white/[0.06] border-white/10" value="1" id="OPT_IN" name="OPT_IN" required />
                  <span className="text-xs text-[#A19D94]/70 font-sans leading-relaxed group-hover:text-[#A19D94] transition-colors">
                    I am happy to receive information from Wedding Video Essex and accept the data privacy statement.
                  </span>
                </label>

                <input type="text" name="email_address_check" value="" style={{ display: "none" }} />
                <input type="hidden" name="locale" value="en" />
                <input type="hidden" name="WEBSITE_LEAD" value="Essex Wedding Video Page" />

                <button
                  type="submit"
                  data-testid="check-date-submit-btn"
                  disabled={submitting}
                  className="w-full py-3.5 bg-[#C2A878] text-[#0A0908] text-sm font-sans font-semibold tracking-wider uppercase hover:bg-[#d4bc92] transition-colors mt-2 disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Secure Your Date"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
